import * as React from 'react';
import { SubscriberService, OnUpdateEvent, createStaticServiceInstance, createUseZenAutoArtService } from "@/services/base-subscriber-service";
import SvgEditService from '@/services/svg-edit-service';
import { BLAZOR_ASSEMBLY } from '@/common/constants';
import { debounce } from '@/common/utilities';
import { IColourTableEntry, recolourCanvas, writeColourTable } from './colours-interop';

/**
 * Façade over the Blazor ColoursService, which owns the spot colours (from the Artwork DNA document for artworks,
 * or the Panel Designer colour table for panels).
 *
 * `spotColours` is a read-only mirror kept up to date by Blazor, so that lookups on hot paths (canvas recolouring,
 * annotations) stay synchronous. The spot colours can only be changed in Blazor (IColoursService.SetSpotColours, used
 * by the SpotColoursDialog).
 */
export default class ColoursService extends SubscriberService<ColoursService> {
    public static Get(comp: React.Component, onUpdate?: OnUpdateEvent) {
        return coloursServiceInstance._RegisterComponent(comp, onUpdate);
    }

    /**
     * Read-only mirror of the Blazor spot colours. Never mutate it or its entries; use {@link SetSpotColours}.
     * @deprecated Use IColoursService.SpotColours in Blazor.
     */
    public spotColours: readonly ISpotColour[] = [];

    private svgEditService: SvgEditService;

    public constructor() {
        super();
    }

    protected Initialized() {
        this.svgEditService = SvgEditService.Get(null, () => this.UpdateCanvasColours());
    }

    /**
     * Writes any pending spot colour changes to the document. Await it before serialising the SVG.
     * @deprecated Use IColoursService.FlushPendingSaveAsync in Blazor.
     */
    public SaveSpotColours(): Promise<void> {
        return DotNet.invokeMethodAsync(BLAZOR_ASSEMBLY, "FlushPendingSpotColourSave");
    }

    /** Recolours the canvas from pe-fill/pe-stroke. @deprecated Will move to Blazor with the canvas. */
    public UpdateCanvasColours = debounce(() => {
        const root = this.svgEditService?.canvas?.getContentElem();
        if (root) {
            recolourCanvas(root);
        }
    });

    /** Called by Blazor whenever the spot colours change. */
    public _applyFromBlazor(colours: ISpotColour[]) {
        this.spotColours = Object.freeze(colours.map(c => Object.freeze({ ...c })));
        this.UpdateCanvasColours();
        this._UpdateSubscribers();
    }

    /** Called by Blazor to write the Panel Designer colour table. */
    public _writeFromBlazor(entries: IColourTableEntry[]) {
        const root = this.svgEditService?.canvas?.getContentElem();
        if (!root) {
            console.warn("Cannot write the colour table - SVGEdit canvas is not available.");
            return;
        }

        writeColourTable(root, entries);
    }

    /** @deprecated Use IColoursService.GetSpotColourWithIdOrName in Blazor. Answers from the mirror. */
    public GetSpotColourWithIdOrName(idOrName: string): ISpotColour | null {
        let nameFoundColour: ISpotColour | null = null;

        for (const col of this.spotColours) {
            if (col.id === idOrName) {
                return col;
            } else if (col.name === idOrName) {
                nameFoundColour = col;
            }
        }

        return nameFoundColour;
    }

    /** @deprecated Use IColoursService.FindSpotColourIdForName in Blazor. Answers from the mirror. */
    public FindSpotColourIdForName(name: string): string | null {
        name = name.trim().toLowerCase();

        for (const col of this.spotColours) {
            if (col.name.toLowerCase() === name) {
                return col.id;
            }
        }

        return null;
    }
}

export const coloursServiceInstance = createStaticServiceInstance(new ColoursService(), "coloursService");

// For React Functional Components
export const useColourService = createUseZenAutoArtService(coloursServiceInstance);
